'use client';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ImageOff, Eye } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

// framer-motion v12 doesn't re-export PanInfo at the package root; inline the
// handful of fields we actually use for drag-gesture handling.
type DragInfo = {
  offset: { x: number; y: number };
  velocity: { x: number; y: number };
};

type ApiGalleryImage = {
  id: string;
  title?: string | null;
  description?: string | null;
  url: string;
  category: string;
  width?: number | null;
  height?: number | null;
};

type ApiCategory = {
  id: string;
  slug: string;
  name: string;
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' as const },
  },
};

// Pretend ratios so skeleton tiles don't all look the same.
const SKELETON_RATIOS = [1, 1.33, 0.75, 1.5, 0.9, 1.2, 0.8, 1.1, 1.4, 0.85];

export default function GalleryPage() {
  const items = useSiteData<ApiGalleryImage[]>('gallery', '/api/gallery', []);
  const dbCategories = useSiteData<ApiCategory[]>(
    'categories-gallery',
    '/api/categories?type=gallery',
    [],
  );

  // Derive a local "initial loading" state. useSiteData returns [] instantly,
  // so we show skeletons until either items arrive or a short grace period
  // passes (in which case the gallery really is empty).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (items.length > 0) {
      setHydrated(true);
      return;
    }
    const t = window.setTimeout(() => setHydrated(true), 600);
    return () => window.clearTimeout(t);
  }, [items.length]);

  const categories = useMemo<string[]>(() => {
    if (dbCategories.length > 0) {
      return ['All', ...dbCategories.map((c) => c.slug)];
    }
    const set = new Set<string>();
    items.forEach((i) => set.add(i.category));
    return ['All', ...Array.from(set)];
  }, [dbCategories, items]);

  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filteredItems = useMemo(
    () =>
      activeCategory === 'All'
        ? items
        : items.filter((item) => item.category === activeCategory),
    [activeCategory, items],
  );

  const selectedIndex = filteredItems.findIndex((i) => i.id === selectedId);
  const selectedItem =
    selectedIndex >= 0 ? filteredItems[selectedIndex] : null;

  const closeLightbox = useCallback(() => setSelectedId(null), []);

  const goPrev = useCallback(() => {
    if (filteredItems.length === 0) return;
    setSelectedId((curr) => {
      const idx = filteredItems.findIndex((i) => i.id === curr);
      if (idx < 0) return curr;
      const prev =
        (idx - 1 + filteredItems.length) % filteredItems.length;
      return filteredItems[prev].id;
    });
  }, [filteredItems]);

  const goNext = useCallback(() => {
    if (filteredItems.length === 0) return;
    setSelectedId((curr) => {
      const idx = filteredItems.findIndex((i) => i.id === curr);
      if (idx < 0) return curr;
      const next = (idx + 1) % filteredItems.length;
      return filteredItems[next].id;
    });
  }, [filteredItems]);

  // Keyboard nav + body scroll lock while the lightbox is open.
  useEffect(() => {
    if (!selectedItem) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedItem, closeLightbox, goPrev, goNext]);

  // Preload neighbours so arrow nav feels instant.
  useEffect(() => {
    if (!selectedItem || filteredItems.length < 2) return;
    const neighbours = [
      filteredItems[(selectedIndex + 1) % filteredItems.length],
      filteredItems[
        (selectedIndex - 1 + filteredItems.length) % filteredItems.length
      ],
    ];
    neighbours.forEach((img) => {
      if (!img?.url) return;
      const pre = new window.Image();
      pre.src = img.url;
    });
  }, [selectedItem, selectedIndex, filteredItems]);

  const handleDragEnd = useCallback(
    (info: DragInfo) => {
      const { offset, velocity } = info;
      const swipePower = (o: number, v: number) => Math.abs(o) * v;
      if (offset.y > 100 && velocity.y > 200) {
        closeLightbox();
        return;
      }
      if (swipePower(offset.x, Math.abs(velocity.x)) < 6000) return;
      if (offset.x < -60) goNext();
      else if (offset.x > 60) goPrev();
    },
    [closeLightbox, goNext, goPrev],
  );

  const showSkeletons = !hydrated && items.length === 0;
  const isEmpty = hydrated && items.length === 0;

  return (
    <motion.div
      className="gallery-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Banner */}
      <section className="gallery-page-hero">
        <div className="gallery-page-hero-blob gallery-page-hero-blob-1" />
        <div className="gallery-page-hero-blob gallery-page-hero-blob-2" />
        <div className="gallery-page-hero-grid" />

        <div className="section-container">
          <motion.div
            className="section-heading gallery-page-hero-heading"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <span className="section-label">GALLERY</span>
            <h1 className="section-title gallery-page-hero-title">
              Visual Showcase
            </h1>
            <p className="section-subtitle">
              A curated collection of screenshots, events, certificates and
              personal moments that shape my creative journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="gallery-page-section">
        <div className="section-container">
          {!isEmpty && !showSkeletons && items.length > 0 && (
            <motion.div
              className="gallery-page-filters"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              {categories.map((category) => {
                const count =
                  category === 'All'
                    ? items.length
                    : items.filter((i) => i.category === category).length;
                const active = activeCategory === category;
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setActiveCategory(category)}
                    className={`gallery-page-filter ${
                      active ? 'gallery-page-filter-active' : ''
                    }`}
                    aria-pressed={active}
                  >
                    <span className="gallery-page-filter-label">
                      {category}
                    </span>
                    <span className="gallery-page-filter-count">({count})</span>
                  </button>
                );
              })}
            </motion.div>
          )}

          {showSkeletons ? (
            <div className="gallery-page-masonry" aria-hidden="true">
              {SKELETON_RATIOS.map((ratio, i) => (
                <div
                  key={i}
                  className="gallery-page-skeleton"
                  style={{ aspectRatio: `${ratio}` }}
                />
              ))}
            </div>
          ) : isEmpty ? (
            <motion.div
              className="gallery-page-empty"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="gallery-page-empty-icon">
                <ImageOff size={40} strokeWidth={1.5} />
              </span>
              <h3 className="gallery-page-empty-title">Gallery coming soon</h3>
              <p className="gallery-page-empty-text">
                Fresh visuals are on the way. Check back shortly.
              </p>
            </motion.div>
          ) : (
            <div className="gallery-page-masonry">
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, i) => {
                  const title = item.title || 'Untitled';
                  const aspect =
                    item.width && item.height && item.height > 0
                      ? item.width / item.height
                      : undefined;
                  return (
                    <motion.button
                      key={item.id}
                      type="button"
                      layout
                      className="gallery-page-item"
                      initial={{ opacity: 0, scale: 0.94, y: 18 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.92 }}
                      transition={{
                        duration: 0.42,
                        delay: Math.min(i * 0.03, 0.35),
                        ease: 'easeOut',
                      }}
                      onClick={() => setSelectedId(item.id)}
                      aria-label={`Open ${title}`}
                    >
                      <div
                        className="gallery-page-thumb"
                        style={
                          aspect ? { aspectRatio: `${aspect}` } : undefined
                        }
                      >
                        <div className="gallery-page-thumb-shine" />
                        {item.url ? (
                          <OptimizedImage
                            src={item.url}
                            alt={title}
                            className="gallery-page-thumb-img"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                            pictureStyle={{
                              position: 'absolute',
                              inset: 0,
                              display: 'block',
                            }}
                            loading="lazy"
                          />
                        ) : null}
                      </div>

                      <div className="gallery-page-overlay">
                        <div className="gallery-page-overlay-top">
                          <span className="gallery-page-chip">
                            {item.category}
                          </span>
                        </div>
                        <div className="gallery-page-overlay-bottom">
                          <h3 className="gallery-page-item-title">{title}</h3>
                          <span className="gallery-page-zoom">
                            <Eye size={16} />
                          </span>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedItem && (
          <Lightbox
            key={selectedItem.id}
            item={selectedItem}
            index={selectedIndex}
            total={filteredItems.length}
            onClose={closeLightbox}
            onPrev={goPrev}
            onNext={goNext}
            onSwipe={handleDragEnd}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function Lightbox({
  item,
  index,
  total,
  onClose,
  onPrev,
  onNext,
  onSwipe,
}: {
  item: ApiGalleryImage;
  index: number;
  total: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSwipe: (info: DragInfo) => void;
}) {
  const title = item.title || 'Untitled';

  return (
    <motion.div
      className="gallery-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={(e) => {
        // Clicks that bubble from the image itself are stopped below.
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Close */}
      <button
        type="button"
        className="gallery-lightbox-btn gallery-lightbox-close"
        onClick={onClose}
        aria-label="Close"
      >
        <X size={20} />
      </button>

      {/* Prev / Next edge buttons */}
      {total > 1 && (
        <>
          <button
            type="button"
            className="gallery-lightbox-btn gallery-lightbox-nav gallery-lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            aria-label="Previous image"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            type="button"
            className="gallery-lightbox-btn gallery-lightbox-nav gallery-lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            aria-label="Next image"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Image — drag-enabled for swipe gestures. */}
      <motion.div
        className="gallery-lightbox-image-wrap"
        onClick={(e) => e.stopPropagation()}
        drag
        dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
        dragElastic={0.25}
        onDragEnd={(_, info) => onSwipe({ offset: info.offset, velocity: info.velocity })}
        initial={{ scale: 0.94, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.94, opacity: 0 }}
        transition={{ type: 'spring' as const, stiffness: 260, damping: 26 }}
      >
        <OptimizedImage
          src={item.url}
          alt={title}
          className="gallery-lightbox-image"
          draggable={false}
          style={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            width: 'auto',
            height: 'auto',
            display: 'block',
            objectFit: 'contain',
            userSelect: 'none',
          }}
          pictureStyle={{
            display: 'inline-block',
            background: 'transparent',
          }}
        />
      </motion.div>

      {/* Caption bar — glass, bottom of viewport. */}
      <motion.div
        className="gallery-lightbox-caption"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gallery-lightbox-caption-left">
          <span className="gallery-lightbox-category">{item.category}</span>
          <div className="gallery-lightbox-text">
            <h3 className="gallery-lightbox-title">{title}</h3>
            {item.description ? (
              <p className="gallery-lightbox-desc">{item.description}</p>
            ) : null}
          </div>
        </div>
        <div className="gallery-lightbox-caption-right">
          <span className="gallery-lightbox-counter">
            {index + 1} / {total}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}
